<template>
  <div class="category-cascader">
    <label v-if="label" :for="id" class="form-label">{{ label }} <span v-if="required" class="text-danger">*</span></label>
    <div class="el-cascader">
      <div class="el-cascader__label" @click="toggleCascader">
        <span v-if="selectedPath.length === 0" class="el-cascader__placeholder">
          {{ placeholder || $t('profile.categories.noParent') || '-- No Parent --' }}
        </span>
        <span v-else class="el-cascader__selected">
          {{ selectedPath.map(item => item.name).join(' / ') }}
        </span>
        <i class="el-icon-arrow-down"></i>
      </div>
      <div v-if="isCascaderOpen" class="el-cascader__dropdown">
        <div ref="cascaderPanels" class="el-cascader__panels">
          <div 
            v-for="(level, index) in cascaderLevels" 
            :key="index"
            class="el-cascader__panel"
          >
            <div v-if="levelLoading && levelLoadingIndex === index" class="el-cascader__loading">
              <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">{{ $t('common.loading') || 'Loading...' }}</span>
              </div>
            </div>
            <ul v-else class="el-cascader__menu">
              <li 
                v-for="item in level" 
                :key="item.id"
                class="el-cascader__menu-item"
                :class="{
                  'is-active': isItemSelected(item, index),
                  'is-disabled': isDisabled(item)
                }"
                @click="selectCascaderItem(item, index)"
              >
                <span>{{ item.code }} - {{ item.name }}</span>
                <i 
                  v-if="item.isHaveChild" 
                  class="el-icon-arrow-right ti ti-chevron-right"
                ></i>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <small v-if="helpText" class="form-text text-muted">
      {{ helpText }}
    </small>
  </div>
</template>

<script>
import { ref, onMounted, watch, nextTick } from 'vue';
import materialCategoryService from '@/services/materialCategoryService';

export default {
  name: 'CategoryCascader',
  props: {
    id: {
      type: String,
      default: () => `category-cascader-${Math.random().toString(36).substr(2, 9)}`
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    helpText: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: String,
      default: ''
    },
    disabledCategoryId: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'category-selected'],
  setup(props, { emit }) {
    const isCascaderOpen = ref(false);
    const cascaderLevels = ref([[]]);
    const selectedPath = ref([]);
    const levelLoading = ref(false);
    const levelLoadingIndex = ref(0);
    const cascaderPanels = ref(null);

    // Close cascader when clicking outside
    const handleClickOutside = (event) => {
      const cascader = event.target.closest('.category-cascader');
      if (!cascader) {
        isCascaderOpen.value = false;
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
      
      // If we have an initial value, load the category path
      if (props.modelValue) {
        loadCategoryPath(props.modelValue);
      } else {
        // Just load the first level categories
        loadRootCategories();
      }
    });

    // Watch for modelValue changes
    watch(() => props.modelValue, (newValue) => {
      if (newValue && !selectedPath.value.find(cat => cat.id === newValue)) {
        loadCategoryPath(newValue);
      } else if (!newValue) {
        selectedPath.value = [];
      }
    });

    // Methods
    const toggleCascader = () => {
      isCascaderOpen.value = !isCascaderOpen.value;
      
      // Always load fresh data when opening the cascader
      if (isCascaderOpen.value) {
        loadRootCategories();
        
        // If we have a selected path, refresh it as well
        if (props.modelValue) {
          loadCategoryPath(props.modelValue);
        }
      }
    };

    const loadRootCategories = async () => {
      levelLoading.value = true;
      levelLoadingIndex.value = 0;
      
      try {
        const response = await materialCategoryService.searchCategories({
          page: 1,
          pageSize: 100,
          sorting: 'name asc'
        });
        
        const rootCategories = response.items.map(item => ({
          ...item,
          isExpanded: false,
          children: []
        }));
        
        cascaderLevels.value = [rootCategories];
      } catch (error) {
        console.error('Error loading root categories:', error);
        emit('error', 'Failed to load categories');
      } finally {
        levelLoading.value = false;
      }
    };

    const loadCategoryChildren = async (parent, level) => {
      if (!parent.isHaveChild) return;
      
      levelLoading.value = true;
      levelLoadingIndex.value = level + 1;
      
      // Trim the levels array to current level + 1
      cascaderLevels.value = cascaderLevels.value.slice(0, level + 1);
      // Add a placeholder for the next level
      cascaderLevels.value.push([]);
      
      try {
        const response = await materialCategoryService.getChildCategories(parent.id);
        const children = response.map(item => ({
          ...item,
          isExpanded: false,
          children: []
        }));
        
        // Update the placeholder with actual children
        if (cascaderLevels.value[level + 1]) {
          cascaderLevels.value[level + 1] = children;
          
          // Scroll the panels to show the new level
          await nextTick();
          if (cascaderPanels.value) {
            cascaderPanels.value.scrollLeft = cascaderPanels.value.scrollWidth;
          }
        } else {
          console.warn(`Cascader level ${level + 1} no longer exists`);
        }
      } catch (error) {
        console.error('Error loading category children:', error);
        emit('error', 'Failed to load subcategories');
      } finally {
        levelLoading.value = false;
      }
    };

    const loadCategoryPath = async (categoryId) => {
      if (!categoryId) {
        selectedPath.value = [];
        return;
      }
      
      levelLoading.value = true;
      selectedPath.value = [];
      
      try {
        // First get the target category
        const targetCategory = await materialCategoryService.getCategoryById(categoryId);
        const parentPath = [];
        
        // Add the target to the path
        parentPath.push(targetCategory);
        
        // If there's a parent, build the full path
        let currentParent = targetCategory.parentId;
        while (currentParent) {
          const parent = await materialCategoryService.getCategoryById(currentParent);
          parentPath.unshift(parent); // Add to beginning of array
          currentParent = parent.parentId;
        }
        
        selectedPath.value = parentPath;
        
        // Load the cascader levels based on the path
        await loadRootCategories();
        
        // Sequentially load children for each level in the path
        for (let i = 0; i < parentPath.length - 1; i++) {
          const currentItem = parentPath[i];
          await loadCategoryChildren(currentItem, i);
        }
      } catch (error) {
        console.error('Error loading category path:', error);
        emit('error', 'Failed to load category path');
      } finally {
        levelLoading.value = false;
      }
    };

    const selectCascaderItem = async (item, level) => {
      // Check if the item is disabled
      if (isDisabled(item)) return;
      
      // Update the selected path
      selectedPath.value = selectedPath.value.slice(0, level);
      selectedPath.value.push(item);
      
      // Emit the selected value
      emit('update:modelValue', item.id);
      emit('category-selected', item);
      
      // If this item has children, load them
      if (item.isHaveChild) {
        await loadCategoryChildren(item, level);
      } else {
        // If it's a leaf node, close the cascader
        isCascaderOpen.value = false;
      }
    };

    const isItemSelected = (item, level) => {
      return selectedPath.value[level]?.id === item.id;
    };
    
    const isDisabled = (item) => {
      return props.disabledCategoryId && item.id === props.disabledCategoryId;
    };

    return {
      isCascaderOpen,
      cascaderLevels,
      selectedPath,
      levelLoading,
      levelLoadingIndex,
      cascaderPanels,
      toggleCascader,
      selectCascaderItem,
      isItemSelected,
      isDisabled
    };
  }
};
</script>

<style scoped>
.el-cascader {
  position: relative;
  display: inline-block;
  width: 100%;
}

.el-cascader__label {
  position: relative;
  display: block;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  cursor: pointer;
}

.el-cascader__placeholder {
  color: #6c757d;
}

.el-cascader__selected {
  color: #495057;
}

.el-icon-arrow-down {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  transition: transform 0.3s;
}

.el-cascader__dropdown {
  position: absolute;
  z-index: 1000;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,.1);
  margin-top: 4px;
  width: max-content; 
  max-width: 100%; 
  max-height: 300px;
}

.el-cascader__panels {
  display: flex;
  flex-wrap: nowrap;
  max-height: 300px;
  overflow-x: auto;
  overflow-y: hidden;
}

.el-cascader__panel {
  min-width: 180px;
  max-width: 220px;
  box-sizing: border-box;
  border-right: 1px solid #e4e7ed;
  height: 100%; 
  overflow-y: auto;
}

.el-cascader__loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
}

.el-cascader__menu {
  margin: 0;
  padding: 0;
  list-style: none;
}

.el-cascader__menu-item {
  position: relative;
  padding: 0.5rem 1rem;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #606266;
}

.el-cascader__menu-item:hover {
  background-color: #f5f7fa;
}

.el-cascader__menu-item.is-active {
  color: #409eff;
  font-weight: 700;
}

.el-cascader__menu-item.is-disabled {
  color: #c0c4cc;
  cursor: not-allowed;
}

.el-icon-arrow-right {
  position: absolute;
  right: 1rem;
  color: #c0c4cc;
}
</style> 